import type { Account } from '@shared/products-types';
import type { ProductWithHelpers } from '@shared/products-core';
import { isCreditCardAccount } from '@shared/products-core';
import type { CardWithHelpers } from '@shared/cards-types';
import { Characteristic } from '@shared/cards-types';
import { getFeatureConfig } from '@luna/feature-config';
import { isAuthorizedCreditCard, isAuthorizedVirtualCreditCard } from '@shared/cards-core';

/**
 * Background:
 *
 * Just as Debit Cards are backed by Debit Accounts, so Credit Cards have
 * backing accounts also.
 *
 * But unlike a Debit Account, a Credit Card's backing account is not a Product,
 * and not accessible to the user.
 *
 * The Credit Card's backing account is a technical "shadow" account on which
 * Transactions are booked, but which the user cannot interact with directly.
 *
 * UI Complexity:
 *
 * This creates a problem for showing Credit Card Transactions in a Transactions
 * History. Since a Transactions History is always presented in the context of
 * an Account Product, what Account Product can we use to present a Credit
 * Card's Transactions?
 *
 * The solution is to use a Main Credit Card Product in place of real Account
 * Product as a proxy for the "shadow" account.
 *
 * Two implications:
 *
 * 1)
 * This means that when attempting to associate a Bank Card Transaction with
 * it's backing account in the user interface, we would look for a corresponding
 * Account Product. But when attempting to do the same for a Credit Card
 * Transaction, we will need to find a corresponding Main Card Product instead.
 *
 * 2)
 * For the same reason, there will also exist Transactions that have no direct
 * corresponding Product.
 *
 * This scenario arises in the context of Transactions made directly on a
 * Credit Card's "shadow" account without use of the Credit Card itself.
 *
 * Examples of such Transactions include:
 *  - Payment of Credit Card invoice.
 *  - Refund on Credit Card payment.
 *  - Charge for Credit Card fees.
 *  - Transfer of funds to Credit Card (to increase available amount).
 *
 * Because all these Transactions bypass the card, and are made directly on the
 * "shadow" account, they will not have a `cardId` property to associate them
 * with any Card product.
 *
 * In these cases we must also associate the Transactions with the Owner's Main
 * Credit Card Product.
 */
export function getCardAccount(
    products: Array<ProductWithHelpers<Account>>,
    cards: Array<CardWithHelpers>,
    ownerId: string,
    cardId?: string,
): ProductWithHelpers<Account> | undefined {
    const card = getCard(products, cards, ownerId, cardId);
    return card && getAccount(products, `${ownerId}-${card.id}`);
}

function getCard(
    products: Array<ProductWithHelpers<Account>>,
    cards: Array<CardWithHelpers>,
    ownerId: string,
    cardId?: string,
): CardWithHelpers | undefined {
    const isCreditCardTransaction = isCreditCardOwner(products, ownerId);

    if (isCreditCardTransaction) {
        return getBackingCreditCard(cards, ownerId, cardId);
    }

    if (!cardId) {
        return undefined;
    }

    return getCardById(cards, cardId) || getMainCard(cards, ownerId);
}

function getBackingCreditCard(
    cards: Array<CardWithHelpers>,
    ownerId: string,
    creditCardId?: string,
): CardWithHelpers | undefined {
    const creditCard = creditCardId ? getCardById(cards, creditCardId) : undefined;

    /**
     * If a Transaction is owned by a Credit Card, but the Transaction has no
     * card associated with it, then the Transaction was made directly on a
     * Credit Card's shadow account.
     *
     * @see getCardAccount()
     */
    const isShadowAccountTransaction = !creditCard;

    if (isShadowAccountTransaction) {
        return getMainCard(cards, ownerId);
    }

    // George can be configured to treat some non-Main Credit Cards as if they
    // were.
    if (shouldTreatAsBackingCreditCard(creditCard)) {
        return creditCard;
    }

    return getMainCard(cards, ownerId);
}

function isCreditCardOwner(products: Array<ProductWithHelpers<Account>>, ownerId: string): boolean {
    const account = getAccount(products, ownerId);
    return account ? isCreditCardAccount(account) : false;
}

/**
 * @see CardFieldsCZ.cz-uniqueId
 */
function getCardById(cards: Array<CardWithHelpers>, cardId: string): CardWithHelpers | undefined {
    return (
        cards.find((card) => cardId === card.id) ||
        cards.find((card) => cardId === card['cz-uniqueId'])
    );
}

function getMainCard(cards: Array<CardWithHelpers>, ownerId: string): CardWithHelpers | undefined {
    return cards.find(
        ({ mainAccount, characteristic }) =>
            mainAccount?.id === ownerId && characteristic === Characteristic.MAIN,
    );
}

function getAccount(
    products: Array<ProductWithHelpers<Account>>,
    ownerId: string,
): ProductWithHelpers<Account> | undefined {
    return products.find(({ id }) => id === ownerId);
}

function shouldTreatAsBackingCreditCard(card: CardWithHelpers): boolean {
    return !isAuthorized(card) || treatAuthorizedCreditCardsAsStandalone();
}

function treatAuthorizedCreditCardsAsStandalone(): boolean {
    const { showAuthorizedAsSubCard } = getFeatureConfig(({ cards }) => cards.credit);
    return !showAuthorizedAsSubCard;
}

function isAuthorized(card: CardWithHelpers): boolean {
    return isAuthorizedCreditCard(card) || isAuthorizedVirtualCreditCard(card);
}
